import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, finalize, mergeMap, shareReplay } from 'rxjs/operators';
import { ConfigService } from '../config';

@Injectable()
export class HttpQueueInterceptor implements HttpInterceptor {
  private requestQueue$ = new Subject<{
    req: HttpRequest<any>;
    next: HttpHandler;
    observer: {
      next: (value: HttpEvent<any>) => void;
      error: (err: any) => void;
      complete: () => void;
    };
    key: string;
  }>();
  private inFlightRequests = new Map<string, Observable<HttpEvent<any>>>();

  constructor(private config: ConfigService) {
    this.requestQueue$
      .pipe(
        mergeMap(
          ({ req, next, observer, key }) => {
            let request$ = this.inFlightRequests.get(key);
            if (!request$) {
              request$ = this.createRetriableRequest$(req, next, key);
              this.inFlightRequests.set(key, request$);
            }
            return new Observable<void>((queueComplete) => {
              request$.subscribe({
                next: (event) => observer.next(event),
                error: (err) => {
                  observer.error(err);
                  queueComplete.complete();
                },
                complete: () => {
                  observer.complete();
                  queueComplete.complete();
                },
              });
            });
          },
          5, // concurrency
        ),
      )
      .subscribe();
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const shouldQueueTheRequest =
      this.config.configuration?.REQUEST_QUEUE?.ENABLED &&
      req.url.includes('api') &&
      !req.url.includes('sentry') &&
      !req.url.includes('storage');

    if (!shouldQueueTheRequest) {
      return next.handle(req);
    }
    const key = this.getRequestKey(req);
    return new Observable<HttpEvent<any>>((observer) => {
      this.requestQueue$.next({ req, next, observer, key });
    });
  }

  private getRequestKey(req: HttpRequest<any>): string {
    // Use method, urlWithParams, and body for uniqueness
    return `${req.method}|${req.urlWithParams}|${JSON.stringify(req.body ?? '')}`;
  }

  private createRetriableRequest$(
    req: HttpRequest<any>,
    next: HttpHandler,
    key: string,
  ): Observable<HttpEvent<any>> {
    let retries = 0;
    const maxRetries = this.config.configuration.REQUEST_QUEUE.MAX_RETRIES;
    const retryDelay = this.config.configuration.REQUEST_QUEUE.RETRY_DELAY; // ms, optional: add delay between retries
    return next.handle(req).pipe(
      catchError((error) => {
        if (
          (error.status === 500 ||
            error.status === 502 ||
            error.status === 503 ||
            error.status === 504) &&
          retries < maxRetries
        ) {
          retries++;
          return timer(retryDelay).pipe(mergeMap(() => next.handle(req)));
        }
        return throwError(() => error);
      }),
      finalize(() => this.inFlightRequests.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }
}
